import { useSeoMeta } from '@unhead/react';
import { useEffect, useRef, useState } from 'react';
import {
  ExternalLink,
  Menu,
  X,
  XCircle,
  AlertTriangle,
  CheckCircle2,
  Shield,
  Globe,
  Lock,
  Eye,
  Server,
  Fingerprint,
  Cookie,
  Ban,
  Route,
  Torus,
} from 'lucide-react';
import { SiteFooter } from '@/components/SiteFooter';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';

/* ------------------------------------------------------------------ */
/*  Icons                                                              */
/* ------------------------------------------------------------------ */
const GithubIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
  </svg>
);

/* ------------------------------------------------------------------ */
/*  Nori SVG Mascot                                                    */
/* ------------------------------------------------------------------ */
const NoriLogo = ({ className = "w-8 h-8" }: { className?: string }) => (
  <svg
    viewBox="0 0 20 16"
    shapeRendering="crispEdges"
    aria-label="Nori the ostrich"
    className={className}
  >
    <g fill="#7B68EE">
      <rect x="8" y="0" width="2" height="1"/>
      <rect x="7" y="1" width="1" height="2"/>
      <rect x="10" y="1" width="1" height="2"/>
      <rect x="6" y="3" width="2" height="1"/>
      <rect x="10" y="3" width="2" height="1"/>
      <rect x="4" y="4" width="10" height="1"/>
      <rect x="3" y="5" width="1" height="3"/>
      <rect x="14" y="5" width="1" height="3"/>
      <rect x="7" y="6" width="2" height="1"/>
      <rect x="4" y="8" width="2" height="1"/>
      <rect x="12" y="8" width="2" height="1"/>
      <rect x="6" y="9" width="6" height="1"/>
      <rect x="6" y="10" width="1" height="1"/>
      <rect x="11" y="10" width="1" height="1"/>
      <rect x="5" y="11" width="2" height="1"/>
      <rect x="11" y="11" width="2" height="1"/>
      <rect x="5" y="12" width="1" height="2"/>
      <rect x="12" y="12" width="1" height="2"/>
      <rect x="4" y="14" width="3" height="1"/>
      <rect x="9" y="14" width="4" height="1"/>
      <rect x="3" y="15" width="1" height="1"/>
      <rect x="6" y="15" width="1" height="1"/>
      <rect x="9" y="15" width="1" height="1"/>
      <rect x="12" y="15" width="1" height="1"/>
    </g>
    <g fill="#C4BBFF">
      <rect x="8" y="1" width="2" height="2"/>
      <rect x="8" y="3" width="2" height="1"/>
      <rect x="4" y="5" width="10" height="1"/>
      <rect x="4" y="6" width="3" height="1"/>
      <rect x="9" y="6" width="5" height="1"/>
      <rect x="4" y="7" width="10" height="1"/>
      <rect x="6" y="8" width="6" height="1"/>
      <rect x="7" y="10" width="4" height="1"/>
      <rect x="7" y="11" width="4" height="1"/>
      <rect x="6" y="12" width="6" height="1"/>
      <rect x="6" y="13" width="1" height="1"/>
      <rect x="9" y="13" width="3" height="1"/>
    </g>
  </svg>
);

/* ------------------------------------------------------------------ */
/*  Status Icons                                                       */
/* ------------------------------------------------------------------ */
const StatusBad = ({ className }: { className?: string }) => (
  <XCircle className={`w-5 h-5 text-red-500 flex-shrink-0 ${className}`} />
);

const StatusCaution = ({ className }: { className?: string }) => (
  <AlertTriangle className={`w-5 h-5 text-amber-500 flex-shrink-0 ${className}`} />
);

const StatusGood = ({ className }: { className?: string }) => (
  <CheckCircle2 className={`w-5 h-5 text-emerald-500 flex-shrink-0 ${className}`} />
);

/* ------------------------------------------------------------------ */
/*  Navigation                                                         */
/* ------------------------------------------------------------------ */
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'About', href: '/' },
    { label: 'Stack', href: '/stack' },
    { label: 'Get Started', href: '/get-started' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || menuOpen
          ? 'bg-background/80 backdrop-blur-xl border-b border-border/50'
          : 'bg-transparent'
      }`}
    >
      <div className="container grid grid-cols-[1fr_1fr] lg:grid-cols-[1fr_auto_1fr] items-center h-16">
        <div className="flex items-center">
          <a href="/" className="flex items-center gap-2.5 group shrink-0">
            <NoriLogo className="w-7 h-7 transition-transform group-hover:scale-110 shrink-0" />
            <span className="font-semibold text-lg tracking-tight whitespace-nowrap">
              nostr-station
            </span>
          </a>
        </div>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-6">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center justify-end gap-3">
          <a
            href="https://github.com/jared-logan/nostr-station"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex"
          >
            <button className="inline-flex items-center justify-center rounded-full h-9 w-9 hover:bg-accent hover:text-accent-foreground transition-colors">
              <GithubIcon className="w-5 h-5" />
            </button>
          </a>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="lg:hidden inline-flex items-center justify-center rounded-full h-9 w-9 hover:bg-accent hover:text-accent-foreground transition-colors"
            aria-label="Toggle menu"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="lg:hidden border-t border-border/50">
          <nav className="container py-4 flex flex-col gap-1">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-2 rounded-lg hover:bg-accent/50"
              >
                {link.label}
              </a>
            ))}
            <a
              href="https://github.com/jared-logan/nostr-station"
              target="_blank"
              rel="noopener noreferrer"
              className="sm:hidden text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-2 rounded-lg hover:bg-accent/50 flex items-center gap-2"
            >
              <GithubIcon className="w-4 h-4" />
              GitHub
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

/* ------------------------------------------------------------------ */
/*  Hero Section                                                       */
/* ------------------------------------------------------------------ */
const HeroSection = () => {
  return (
    <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden pt-16">
      {/* Background effects */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-10%,hsl(265_85%_60%/0.15),transparent)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_600px_at_50%_50%,hsl(265_70%_55%/0.08),transparent)]" />

      {/* Grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `linear-gradient(hsl(265 85% 60%) 1px, transparent 1px), linear-gradient(90deg, hsl(265 85% 60%) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }}
      />

      <div className="container relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Eyebrow */}
          <span className="text-primary text-sm font-medium tracking-wider uppercase mb-4 block">
            Nsite Browser
          </span>

          {/* Title */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6">
            Browse <span className="text-gradient">verified</span> sites
          </h1>

          {/* Description */}
          <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            The nsite browser built into nostr-station fetches sites directly from Nostr relays and Blossom servers — no gateway, no trust, just signed content.
          </p>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/*  Comparison Table                                                   */
/* ------------------------------------------------------------------ */
type Status = 'bad' | 'caution' | 'good';

interface ComparisonRow {
  axis: string;
  icon: React.ReactNode;
  normalHttps: { status: Status; text: string };
  publicGateway: { status: Status; text: string };
  nostrStation: { status: Status; text: string };
}

const comparisonData: ComparisonRow[] = [
  {
    axis: 'Content integrity',
    icon: <Shield className="w-4 h-4" />,
    normalHttps: {
      status: 'bad',
      text: 'Trust host + CA. Host/CDN can swap content silently.',
    },
    publicGateway: {
      status: 'caution',
      text: 'Gateway may hash-verify, but you have to trust it to do so honestly.',
    },
    nostrStation: {
      status: 'good',
      text: 'Verified locally — fetchBlob SHA256-checks every blob against the signed manifest before rendering (src/lib/nsite-resolver.ts:870).',
    },
  },
  {
    axis: 'Who can tamper',
    icon: <Eye className="w-4 h-4" />,
    normalHttps: {
      status: 'bad',
      text: 'Host, CDN, anyone with a mis-issued cert.',
    },
    publicGateway: {
      status: 'bad',
      text: 'The gateway operator (can lie about hash check, inject scripts, rewrite HTML).',
    },
    nostrStation: {
      status: 'good',
      text: 'Nobody. Mismatches surface as hash_mismatch and fall through to the next Blossom server.',
    },
  },
  {
    axis: 'Transport security',
    icon: <Route className="w-4 h-4" />,
    normalHttps: {
      status: 'good',
      text: 'One TLS connection to one origin.',
    },
    publicGateway: {
      status: 'caution',
      text: 'TLS to the gateway is fine, but the gateway makes its own backend calls on your behalf.',
    },
    nostrStation: {
      status: 'good',
      text: 'TLS WebSockets to relays + TLS HTTPS to Blossom. No gateway in the path.',
    },
  },
  {
    axis: 'Who sees your IP + what you fetch',
    icon: <Eye className="w-4 h-4" />,
    normalHttps: {
      status: 'caution',
      text: 'The host sees your IP and every URL path.',
    },
    publicGateway: {
      status: 'bad',
      text: 'The gateway sees your IP, the full path, and which npub you\'re viewing; your ISP sees the npub via SNI.',
    },
    nostrStation: {
      status: 'caution',
      text: 'Trust spread across many operators — relays see "queried npub X," Blossom servers see opaque <sha256> blob requests. Better against any one operator, broader surface against a network observer correlating across them.',
    },
  },
  {
    axis: 'Path / URL leakage',
    icon: <Lock className="w-4 h-4" />,
    normalHttps: {
      status: 'bad',
      text: 'Full path visible to the host.',
    },
    publicGateway: {
      status: 'bad',
      text: 'Full path visible to the gateway; npub leaks via SNI.',
    },
    nostrStation: {
      status: 'good',
      text: 'After the manifest, only opaque blob hashes leave your machine. (Initial manifest query does reveal npub + site-name to relays.)',
    },
  },
  {
    axis: 'Iframe origin',
    icon: <Globe className="w-4 h-4" />,
    normalHttps: {
      status: 'caution',
      text: "Real origin; author's CSP applies (often permissive).",
    },
    publicGateway: {
      status: 'caution',
      text: 'Gateway-controlled origin; gateway picks CSP.',
    },
    nostrStation: {
      status: 'good',
      text: 'Unique <siteId>.nsite.localhost per site, isolated from the dashboard by SOP.',
    },
  },
  {
    axis: 'CSP enforced on the site',
    icon: <Shield className="w-4 h-4" />,
    normalHttps: {
      status: 'bad',
      text: 'Whatever the author wrote — in practice, usually permissive.',
    },
    publicGateway: {
      status: 'bad',
      text: 'Whatever the gateway chooses — usually permissive.',
    },
    nostrStation: {
      status: 'good',
      text: 'Strict default: self, wss:, local ws, data:/blob: only (src/lib/routes/nsite.ts:581). Relaxed to https: only for pubkeys you\'ve added to trustedExternalNsites.',
    },
  },
  {
    axis: 'Can the page beacon to third parties?',
    icon: <Ban className="w-4 h-4" />,
    normalHttps: {
      status: 'bad',
      text: 'Yes, freely.',
    },
    publicGateway: {
      status: 'bad',
      text: 'Yes, freely.',
    },
    nostrStation: {
      status: 'good',
      text: 'No, unless the author is on your trusted list. Trackers and analytics are blocked by CSP.',
    },
  },
  {
    axis: 'Cookies / storage scope',
    icon: <Cookie className="w-4 h-4" />,
    normalHttps: {
      status: 'caution',
      text: 'Shared with everything else on that origin.',
    },
    publicGateway: {
      status: 'caution',
      text: 'Each nsite gets its own subdomain origin (isolated by SOP), but parent-domain cookies and embedded trackers can correlate across nsites on the same gateway.',
    },
    nostrStation: {
      status: 'good',
      text: 'Separate localStorage / IndexedDB per nsite — every site gets its own *.nsite.localhost origin.',
    },
  },
  {
    axis: 'Censorship resistance',
    icon: <Server className="w-4 h-4" />,
    normalHttps: {
      status: 'bad',
      text: 'Single host, registrar, DNS — one block kills it.',
    },
    publicGateway: {
      status: 'caution',
      text: 'Multiple gateways exist, but each is itself blockable.',
    },
    nostrStation: {
      status: 'good',
      text: 'No gateway dependency. Auto-fallback across multiple relays and Blossom servers.',
    },
  },
  {
    axis: "Publisher anonymity'",
    icon: <Fingerprint className="w-4 h-4" />,
    normalHttps: {
      status: 'bad',
      text: 'Registrar, WHOIS, hosting account, often KYC.',
    },
    publicGateway: {
      status: 'good',
      text: 'Just an npub + Blossom upload.',
    },
    nostrStation: {
      status: 'good',
      text: 'Same — no registrar, no hosting account, no KYC.',
    },
  },
  {
    axis: 'Author identity proof',
    icon: <Fingerprint className="w-4 h-4" />,
    normalHttps: {
      status: 'caution',
      text: "CA's claim about who owns the domain.",
    },
    publicGateway: {
      status: 'caution',
      text: 'Signature on the Nostr event, verified by the gateway (you trust the gateway to verify honestly).',
    },
    nostrStation: {
      status: 'good',
      text: 'Signature on the Nostr event, verified locally.',
    },
  },
  {
    axis: 'Tor / proxy support',
    icon: <Torus className="w-4 h-4" />,
    normalHttps: {
      status: 'good',
      text: 'Open it in Tor Browser if you want.',
    },
    publicGateway: {
      status: 'good',
      text: 'Open it in Tor Browser if you want.',
    },
    nostrStation: {
      status: 'bad',
      text: 'None wired up; direct connections only. (On the roadmap.)',
    },
  },
];

const StatusCell = ({ status, text }: { status: Status; text: string }) => {
  const Icon = status === 'bad' ? StatusBad : status === 'caution' ? StatusCaution : StatusGood;
  const label = status === 'bad' ? 'Poor' : status === 'caution' ? 'Caution' : 'Good';

  return (
    <div className="flex items-start gap-2.5">
      <Icon className="mt-0.5" />
      <div>
        <span className="sr-only">{label}</span>
        <span className="text-sm text-muted-foreground leading-relaxed">{text}</span>
      </div>
    </div>
  );
};

const ComparisonSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="comparison" ref={sectionRef} className="py-24 sm:py-32 relative">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_50%_50%,hsl(265_85%_60%/0.06),transparent)]" />

      <div className="container relative">
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-primary text-sm font-medium tracking-wider uppercase mb-4 block">
            Security Comparison
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
            How we <span className="text-gradient">compare</span>
          </h2>
          <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
            See how nostr-station's built-in nsite browser stacks up against normal HTTPS and public nsite gateways.
          </p>
        </div>

        <div className={`transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="rounded-2xl border border-border/50 bg-card/50 backdrop-blur-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/50">
                    <th className="text-left px-6 py-4 font-medium text-primary uppercase tracking-wider text-xs whitespace-nowrap">
                      Axis
                    </th>
                    <th className="text-left px-6 py-4 font-medium text-primary uppercase tracking-wider text-xs min-w-[240px]">
                      Normal HTTPS site in normal browser
                    </th>
                    <th className="text-left px-6 py-4 font-medium text-primary uppercase tracking-wider text-xs min-w-[240px]">
                      Nsite via public gateway in normal browser
                    </th>
                    <th className="text-left px-6 py-4 font-medium text-primary uppercase tracking-wider text-xs min-w-[240px]">
                      Nsite in nostr-station browser
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.map((row, index) => (
                    <tr
                      key={row.axis}
                      className={`border-b border-border/30 hover:bg-primary/5 transition-colors ${index === comparisonData.length - 1 ? 'border-b-0' : ''}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2 text-foreground font-medium">
                          <span className="text-primary">{row.icon}</span>
                          {row.axis}
                        </div>
                      </td>
                      <td className="px-6 py-4 align-top">
                        <StatusCell status={row.normalHttps.status} text={row.normalHttps.text} />
                      </td>
                      <td className="px-6 py-4 align-top">
                        <StatusCell status={row.publicGateway.status} text={row.publicGateway.text} />
                      </td>
                      <td className="px-6 py-4 align-top">
                        <StatusCell status={row.nostrStation.status} text={row.nostrStation.text} />
                      </td>
                    </tr>
                  ))}
                  {/* Summary row */}
                  <tr className="border-t-2 border-border/50 bg-primary/5">
                    <td className="px-6 py-4 font-semibold text-foreground whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-primary" />
                        Summary
                      </div>
                    </td>
                    <td className="px-6 py-4 align-top">
                      <div className="flex items-start gap-2.5">
                        <StatusCaution className="mt-0.5" />
                        <span className="text-sm text-muted-foreground leading-relaxed">
                          Convenient and familiar. Weak integrity, permissive sandbox, but Nostr/relays learn nothing about you.
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 align-top">
                      <div className="flex items-start gap-2.5">
                        <StatusBad className="mt-0.5" />
                        <span className="text-sm text-muted-foreground leading-relaxed">
                          Worst of both worlds. Adds a fully-trusted middleman (the gateway) on top of normal HTTPS risks. Avoid.
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 align-top">
                      <div className="flex items-start gap-2.5">
                        <StatusGood className="mt-0.5" />
                        <span className="text-sm text-muted-foreground leading-relaxed">
                          Best overall integrity + sandboxing. Verified bytes, tight CSP, trust split across many operators. Trade-offs: relays learn which npubs you browse, and no Tor support yet.
                        </span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/*  How It Works Section                                               */
/* ------------------------------------------------------------------ */
const HowItWorksSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const steps = [
    {
      icon: Server,
      title: 'Fetch manifest',
      description: 'The browser queries Nostr relays for the signed nsite manifest (kind 34128 / 35128 / 15128) associated with the npub or NIP-05 you typed.',
    },
    {
      icon: Shield,
      title: 'Verify signature',
      description: 'The manifest signature is verified locally against the npub. No gateway to trust.',
    },
    {
      icon: Lock,
      title: 'Fetch blobs',
      description: 'Each asset is fetched from Blossom servers by SHA256 hash. Content is checked against the manifest.',
    },
    {
      icon: Globe,
      title: 'Render isolated',
      description: 'The site renders in a unique .nsite.localhost origin with strict CSP that blocks external scripts, trackers, and beacons by default — isolated from the dashboard by same-origin policy.',
    },
  ];

  return (
    <section id="how-it-works" ref={sectionRef} className="py-24 sm:py-32 relative">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl" />

      <div className="container relative">
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-primary text-sm font-medium tracking-wider uppercase mb-4 block">
            How it works
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
            Four steps to <span className="text-gradient">verified content</span>
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, index) => (
            <div
              key={step.title}
              className={`group relative p-6 rounded-2xl border border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/30 transition-all duration-500 hover:glow-purple ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="w-12 h-12 rounded-xl bg-primary/15 flex items-center justify-center mb-4 transition-transform group-hover:scale-110">
                <step.icon className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-2">{step.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {step.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/*  What's In The Browser Section                                      */
/* ------------------------------------------------------------------ */
const WhatsInBrowserSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const features = [
    'Tabbed browsing with per-tab history (back / forward / reload)',
    'Address bar accepting npub, hex pubkey, NIP-05, NSIT names, or gateway URLs',
    'Persistent bookmarks',
    'Site Info pane (pubkey, manifest metadata, relays, trust state)',
    'Diagnostics (CSP violations, script errors, sandbox status, Blossom server tier)',
    'Per-author trust toggle for relaxing CSP on sites you trust',
  ];

  return (
    <section id="browser-features" ref={sectionRef} className="py-24 sm:py-32 relative">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl" />

      <div className="container relative">
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-primary text-sm font-medium tracking-wider uppercase mb-4 block">
            What's in the browser
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
            A real browser, <span className="text-gradient">not a preview iframe</span>
          </h2>
        </div>

        <div className="max-w-3xl mx-auto space-y-4">
          {features.map((feature, index) => (
            <div
              key={feature}
              className={`group relative p-5 rounded-2xl border border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/30 transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="flex items-center gap-4">
                <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                <span className="text-sm text-muted-foreground leading-relaxed">
                  {feature}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/*  Roadmap Section                                                    */
/* ------------------------------------------------------------------ */
const RoadmapSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="roadmap" ref={sectionRef} className="py-24 sm:py-32 relative">
      <div className="container">
        <div className={`text-center mb-12 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-primary text-sm font-medium tracking-wider uppercase mb-4 block">
            Roadmap
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
            What's <span className="text-gradient">coming</span>
          </h2>
        </div>

        <div className={`max-w-3xl mx-auto transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="relative rounded-2xl border border-dashed border-primary/30 bg-primary/5 p-6 sm:p-8">
            <div className="absolute -top-3 left-6">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-medium">
                <Shield className="w-3.5 h-3.5" />
                On the roadmap
              </span>
            </div>
            <div className="grid gap-4 pt-2">
              <div className="flex items-start gap-3">
                <span className="text-lg flex-shrink-0 mt-0.5">🧅</span>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  <strong className="text-foreground">Tor / SOCKS proxy support.</strong> Route relay and Blossom traffic through a configurable proxy for full reader privacy.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-lg flex-shrink-0 mt-0.5">📚</span>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  <strong className="text-foreground">Global history and search.</strong> Per-tab history exists today; cross-tab history and search are next.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-lg flex-shrink-0 mt-0.5">🌐</span>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  <strong className="text-foreground">Discovery feed.</strong> Browse nsites published by npubs you follow, without leaving the dashboard.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/*  Main Page                                                          */
/* ------------------------------------------------------------------ */
const Nsite = () => {
  useSeoMeta({
    title: 'Nsite Browser — nostr-station',
    description: 'The nsite browser built into nostr-station fetches sites directly from Nostr relays and Blossom servers — no gateway, no trust, just signed content.',
    ogTitle: 'Nsite Browser — nostr-station',
    ogDescription: 'The nsite browser built into nostr-station fetches sites directly from Nostr relays and Blossom servers — no gateway, no trust, just signed content.',
    twitterCard: 'summary_large_image',
  });

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navigation />
      <HeroSection />
      <HowItWorksSection />
      <WhatsInBrowserSection />
      <ComparisonSection />
      <RoadmapSection />
      <SiteFooter />
    </div>
  );
};

export default Nsite;
